<template>
  <generic-container
    :title="$t('report composer')"
    icon="line chart"
    :header_buttons="header_buttons"
    ref="container"
  >
    <div class="ui container">
      <div class="ui five item pointing menu">
        <a data-tab="general" class="item active">
          <i class="info icon"></i>
          {{ $t("general") }}
        </a>
        <a data-tab="input_screen" class="item">
          <i class="keyboard icon"></i>
          {{ $t("input") }}
        </a>
        <a data-tab="result_screen" class="item">
          <i class="poll icon"></i>
          {{ $t("output") }}
        </a>
        <a data-tab="database_query" class="item">
          <i class="database icon"></i>
          {{ $t("database") }}
        </a>
        <a data-tab="permissions" class="item">
          <i class="key icon"></i>
          {{ $t("permissions") }}
        </a>
      </div>
      <div data-tab="general" class="ui bottom attached tab active">
        <form class="ui form">
          <div class="fields">
            <div class="eight wide field">
              <label>{{ $t("name") }}</label>
              <input
                type="text"
                name="name"
                v-model="name"
                :placeholder="$t('name')"
              />
            </div>
            <div class="eight wide field">
              <label>{{ $t("module") }}</label>
              <div class="ui selection dropdown" ref="module_dropdown">
                <input type="hidden" name="module" />
                <i class="dropdown icon"></i>
                <div class="default text">{{ $t("module") }}</div>
                <div class="menu"></div>
              </div>
            </div>
          </div>
          <div class="field">
            <label>{{ $t("anonymous") }}</label>
            <div class="ui toggle checkbox">
              <input
                type="checkbox"
                name="allow_anonymous"
                v-model="setup.allow_anonymous"
              />
              <label>{{ $t("allow anonymous access to this report") }}</label>
            </div>
          </div>
          <a
            :href="external_url"
            target="_blank"
            v-if="setup.allow_anonymous"
            >{{ external_url }}</a
          >

          <div class="field">
            <label>{{ $t("description") }}</label>
            <textarea
              name="description"
              v-model="description"
              rows="2"
            ></textarea>
          </div>
        </form>
      </div>
      <div data-tab="input_screen" class="ui bottom attached tab">
        <div class="ui tabular menu">
          <a data-tab="input_screen_html" class="item active">
            <i class="html5 icon"></i>
            {{ $t("html") }}
          </a>
          <a data-tab="input_screen_js" class="item">
            <i class="js icon"></i>
            {{ $t("javascript") }}
          </a>
        </div>
        <div data-tab="input_screen_html" class="ui bottom attached tab active">
          <code-editor
            v-model="setup.input_screen_html"
            type="html"
            icon="html5"
            theme="monokai"
            ref="input_html_code_editor"
            :title="$t('html code')"
            :show_format="true"
            :header_menus="html_header_menus('input_html_code_editor')"
          ></code-editor>
          <div class="ui dividing header" v-if="setup.input_screen_html">
            {{ $t("preview") }}
          </div>
          <div ref="input_preview" v-html="setup.input_screen_html"></div>
        </div>
        <div data-tab="input_screen_js" class="ui bottom attached tab">
          <code-editor
            v-model="setup.input_screen_js"
            type="javascript"
            icon="js"
            theme="monokai"
            ref="input_mixin_code_editor"
            :title="$t('mixin')"
            :show_format="true"
            :header_buttons="hButtonsInput"
            :header_menus="mixin_header_menus('input_mixin_code_editor')"
            ><div class="ui info message">
              <div class="header">{{ $t("hints") }}</div>
              <ul class="list">
                <li
                  v-html="
                    $t(
                      'define an object which contains a data attribute, methods and everything you need to set the functionality',
                    )
                  "
                ></li>
                <li
                  v-html="
                    $t('please refer to vue mixin site for more information')
                  "
                ></li>
                <li
                  v-html="
                    $t(
                      'the attributes of object returned by data function will be sent as parameters to database query',
                    )
                  "
                ></li>
              </ul></div
          ></code-editor>
        </div>
      </div>
      <div data-tab="result_screen" class="ui bottom attached tab">
        <div class="ui tabular menu">
          <a data-tab="result_screen_html" class="item active">
            <i class="html5 icon"></i>
            {{ $t("html") }}
          </a>
          <a data-tab="result_screen_js" class="item">
            <i class="js icon"></i>
            {{ $t("javascript") }}
          </a>
        </div>
        <div
          data-tab="result_screen_html"
          class="ui bottom attached tab active"
        >
          <code-editor
            v-model="setup.result_screen_html"
            type="html"
            icon="html5"
            theme="monokai"
            ref="result_html_code_editor"
            :title="$t('html code')"
            :show_format="true"
            :header_menus="html_header_menus('result_html_code_editor')"
          ></code-editor>

          <div class="ui dividing header" v-if="setup.result_screen_html">
            {{ $t("preview") }}
          </div>
          <div ref="result_preview" v-html="setup.result_screen_html"></div>
        </div>
        <div data-tab="result_screen_js" class="ui bottom attached tab">
          <code-editor
            v-model="setup.result_screen_js"
            type="javascript"
            icon="js"
            theme="monokai"
            ref="result_mixin_code_editor"
            :title="$t('mixin')"
            :show_format="true"
            :header_buttons="hButtonsResult"
            :header_menus="mixin_header_menus('result_mixin_code_editor')"
            ><div class="ui info message">
              <div class="header">{{ $t("hints") }}</div>
              <ul class="list">
                <li
                  v-html="
                    $t(
                      'define an object which contains a data attribute, methods and everything you need to set the functionality',
                    )
                  "
                ></li>
                <li
                  v-html="
                    $t('please refer to vue mixin site for more information')
                  "
                ></li>
                <li
                  v-html="
                    $t(
                      'refer to result variable to get the result of database query',
                    )
                  "
                ></li>
              </ul></div
          ></code-editor>
        </div>
      </div>
      <div data-tab="database_query" class="ui bottom attached tab">
        <code-editor
          v-model="setup.database_query"
          type="javascript"
          icon="database"
          theme="monokai"
          ref="database_code_editor"
          :title="$t('database query')"
          :show_format="true"
          :header_menus="database_header_menus('database_code_editor')"
          ><div class="ui info message">
            <div class="header">{{ $t("hints") }}</div>
            <ul class="list">
              <li v-html="$t('use reject(error) when exception')"></li>
              <li v-html="$t('use resolve(data) to return data')"></li>
              <li v-html="$t('refer to parameters using :param_name')"></li>
            </ul></div
        ></code-editor>
      </div>
      <div data-tab="permissions" class="ui bottom attached tab">
        <table class="ui compact celled definition table">
          <thead>
            <tr>
              <th></th>
              <th>{{ $t("role") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(role, index) in roles" :key="index">
              <td class="collapsing">
                <div class="ui fitted slider checkbox">
                  <input type="checkbox" v-model="roles[index].granted" />
                  <label></label>
                </div>
              </td>
              <td>{{ role.name }}</td>
            </tr>
          </tbody>
          <tfoot class="full-width">
            <tr>
              <th></th>
              <th colspan="4">
                <div
                  class="ui right floated small primary labeled icon button"
                  @click="apply_permissions"
                >
                  <i class="check icon"></i>
                  {{ $t("apply") }}
                </div>
              </th>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
    <example-code-modal
      :example_code="example_code.text"
      :type="example_code.type"
      v-if="example_code.visible"
      @destroy="example_code.visible = false"
    />
    <import-modal ref="import_modal" />
  </generic-container>
</template>

<script>
import {
  local_api,
  error_toast,
  success_toast,
  warning_toast,
  download_content,
} from "@/utils";
import filters from "@/filters";
import GenericContainer from "@/components/GenericContainer";
import ExampleCodeModal from "@/components/ExampleCodeModal";
import CodeEditor from "@/components/code_editor/Index";
import moment from "moment";
import ImportModal from "./import_modal/Index";

export default {
  name: "ReportComposer",
  components: { GenericContainer, CodeEditor, ExampleCodeModal, ImportModal },
  filters: filters,
  props: {
    templates: Array,
  },
  data() {
    return {
      uuid: "",
      name: "",
      description: "",
      module: "",
      setup: {
        input_screen_html: "",
        input_screen_js: this.default_mixin,
        result_screen_html: "",
        result_screen_js: this.default_mixin,
        database_query: "",
        allow_anonymous: false,
      },
      modules: [],
      roles: [],
      header_buttons: [
        { icon: "blue save", click: this.save, tooltip: this.$t("save") },
        { icon: "file export", click: this.export, tooltip: this.$t("export") },
        { icon: "file import", click: this.import, tooltip: this.$t("import") },
        { icon: "red trash", click: this.remove, tooltip: this.$t("delete") },
      ],
      example_code: {
        visible: false,
        text: "",
        type: "",
      },
      loading: false,
    };
  },
  computed: {
    default_mixin: function () {
      return `{
    data: function() {
        return {
        }
    },
    methods: {
    },
    created: function() {
    },
    mounted: function() {
    }
}`;
    },
    hButtonsInput: function () {
      let vm = this;
      return [
        {
          icon: "help",
          tooltip: this.$t("example code"),
          click: function () {
            vm.show_example(vm.default_mixin);
          },
        },
      ];
    },
    hButtonsResult: function () {
      let vm = this;
      return [
        {
          icon: "help",
          tooltip: this.$t("example code"),
          click: function () {
            vm.show_example(vm.default_mixin);
          },
        },
      ];
    },
    external_url() {
      let vm = this;
      if (vm.uuid) {
        return `${window.location.origin}/#/report/anonymous?i=${vm.uuid}`;
      } else return "";
    },
  },
  methods: {
    html_header_menus(ref) {
      let vm = this;
      return [
        {
          tooltip: this.$t("format code"),
          icon: "wrench",
          buttons: this.templates
            .filter((t) => t.type == "html")
            .map((n) => {
              return {
                icon: n.icon,
                text: n.name,
                click: function () {
                  vm.$refs[ref].insert_content(n.template);
                },
              };
            }),
        },
      ];
    },
    mixin_header_menus(ref) {
      let vm = this;
      return [
        {
          tooltip: this.$t("format code"),
          icon: "wrench",
          buttons: this.templates
            .filter((t) => t.type == "mixin")
            .map((n) => {
              return {
                icon: n.icon,
                text: n.name,
                click: function () {
                  vm.$refs[ref].insert_content(n.template);
                },
              };
            }),
        },
      ];
    },
    database_header_menus() {
      let vm = this;
      return [
        {
          tooltip: this.$t("format code"),
          icon: "wrench",
          buttons: this.templates
            .filter((t) => t.type == "database")
            .map((n) => {
              return {
                icon: n.icon,
                text: n.name,
                click: function () {
                  vm.$refs.database_code_editor.insert_content(n.template);
                },
              };
            }),
        },
      ];
    },
    init_code(type) {
      require(`brace/mode/${type}`);
      require("brace/theme/monokai");
    },
    update_input_preview(a) {
      let vm = this;
      window.jQuery(vm.$refs.input_preview).html(a);
    },
    update_result_preview(a) {
      let vm = this;
      window.jQuery(vm.$refs.result_preview).html(a);
    },
    load_report(report) {
      let vm = this;

      vm.roles.map((r) => {
        r.granted = false;
      });

      vm.loading = true;
      local_api(vm, "post", "/report/do-get", {
        i: report.uuid,
      })
        .then((r) => {
          vm.loading = false;
          vm.uuid = report.uuid;
          vm.name = r.name;
          vm.description = r.description;
          vm.setup.input_screen_html = r.setup.input_screen_html || "";
          vm.setup.input_screen_js =
            r.setup.input_screen_js || vm.default_mixin;
          vm.setup.result_screen_html = r.setup.result_screen_html || "";
          vm.setup.result_screen_js =
            r.setup.result_screen_js || vm.default_mixin;
          vm.setup.database_query = r.setup.database_query || "";
          vm.setup.allow_anonymous = r.setup.allow_anonymous;
          if (r.module_id) {
            vm.module = r.module_id;
            window
              .jQuery(vm.$refs.module_dropdown)
              .dropdown("set selected", r.module_id);
          }

          r.rpt_suscriptions.map((s) => {
            let trgt = vm.roles.find((r) => r.role_id == s.role_id);
            if (trgt) trgt.granted = true;
          });

          vm.$emit("report_loaded");
        })
        .catch((err) => {
          vm.loading = false;
          console.error(err);
        });
    },
    get_report_data() {
      return {
        uuid: this.uuid,
        name: this.name,
        allow_anonymous: this.allow_anonymous,
        module: this.module,
        description: this.description,
        setup: this.setup,
      };
    },
    apply_permissions() {
      let vm = this;
      let granted = vm.roles
        .filter((r) => r.granted)
        .map((r) => {
          return r.role_id;
        });
      if (vm.uuid) {
        local_api(vm, "post", "/report_composer/do-apply-suscriptions", {
          i: vm.uuid,
          r: granted,
        })
          .then(() => {
            vm.$alertify.success(vm.$t("suscriptions configured"));
          })
          .catch((err) => {
            error_toast(err);
          });
      } else this.$alertify.warning(this.$t("no report selected yet"));
    },
    save() {
      let vm = this;
      let data = vm.get_report_data();
      local_api(vm, "post", "/report_composer/do-save", data)
        .then(() => {
          success_toast(vm.$t("report saved"));
        })
        .catch((err) => {
          error_toast(err);
        });
    },
    show_example(code) {
      this.example_code.visible = true;
      this.example_code.text = code;
      this.example_code.type = "javascript";
    },
    export() {
      let vm = this;
      if (vm.uuid) {
        local_api(vm, "post", "/report_composer/do-export", { u: vm.uuid })
          .then((rdata) => {
            download_content(
              rdata,
              `export_${rdata.name}_${moment().format("YYYY-MM-DD_HH-mm")}.rpt`,
            );
          })
          .catch((err) => {
            error_toast(err);
          });
      } else {
        warning_toast(
          vm.$t("report must be saved first in order to export it"),
        );
      }
    },
    import() {
      let vm = this;
      vm.$refs.import_modal.show_modal();
    },
    remove() {
      let vm = this;
      if (vm.uuid) {
        vm.$alertify.confirm(
          vm.$t("are you sure to remove report?"),
          function () {
            local_api(vm, "post", "/report_composer/do-remove", {
              u: vm.uuid,
            })
              .then(() => {
                success_toast(vm.$t("report deleted"));
                vm.$emit("deleted");
              })
              .catch((err) => {
                error_toast(err);
              });
          },
        );
      } else {
        warning_toast(vm.$t("report must be selected in order to delete it"));
      }
    },
  },
  created() {
    let vm = this;
    local_api(vm, "post", "/system/do-get-modules")
      .then((r) => {
        vm.modules = r;
        let vls = r
          .map((m) => {
            return {
              name: m.setup.name,
              value: m.module_id,
            };
          })
          .sort((a, b) => {
            if (a.name > b.name) return 1;
            if (a.name < b.name) return -1;
            return 0;
          });

        window
          .jQuery(vm.$refs.module_dropdown)
          .dropdown({
            onChange(value) {
              vm.module = value;
            },
          })
          .dropdown("setup menu", { values: vls });
      })
      .catch((err) => {
        vm.$alertify.error(err);
      });

    local_api(vm, "post", "/user_role/do-get-all")
      .then((_roles) => {
        vm.roles = _roles.map((r) => {
          r.granted = false;
          return r;
        });
      })
      .catch((err) => {
        console.error(err);
      });
  },
  mounted() {
    // let vm = this;
    window.jQuery(".ui.pointing.menu .item").tab();
    window.jQuery(".ui.tabular.menu .item").tab();

    window.jQuery(".hintbtn").popup({
      on: "click",
    });
  },
  watch: {
    loading() {
      this.$refs.container.set_loading(this.loading);
    },
  },
};
</script>
